import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Skeleton,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime } from '@/lib/utils';
import { MFA_DEVICES_KEY, MFA_STATUS_KEY, mfaApi, toFactor } from './mfa-api';

/**
 * אימות דו-שלבי: הפעלה (QR + מפתח ידני → קוד → קודי גיבוי), כיבוי, קודי גיבוי
 * חדשים ומכשירים שנזכרו. השרת הוא המקור — הסוד לא נשמר בדפדפן, וקודי הגיבוי
 * מוצגים פעם אחת בלבד.
 */
export function TwoStepCard() {
  const { t } = useTranslation(['auth', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const { refreshSession, updateSession } = useAuth();
  const status = useQuery({ queryKey: MFA_STATUS_KEY, queryFn: ({ signal }) => mfaApi.status(signal) });
  const devices = useQuery({
    queryKey: MFA_DEVICES_KEY,
    queryFn: ({ signal }) => mfaApi.devices(signal),
    enabled: status.data?.enabled === true,
  });
  const [setupOpen, setSetupOpen] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [confirm, setConfirm] = useState<'disable' | 'regenerate' | null>(null);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: MFA_STATUS_KEY });
    await qc.invalidateQueries({ queryKey: MFA_DEVICES_KEY });
  };

  const revoke = useMutation({
    mutationFn: (id: string) => (id === 'all' ? mfaApi.revokeAllDevices() : mfaApi.revokeDevice(id)),
    onSuccess: async () => {
      toast.success(t('mfa.toast.deviceRevoked'));
      await refresh();
    },
    onError: (e) => toast.error(t('mfa.toast.failed'), e instanceof ApiError ? e.message : undefined),
  });

  const s = status.data;
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-accent" aria-hidden />
            {t('mfa.card.title')}
          </CardTitle>
          <CardDescription>{t('mfa.card.description')}</CardDescription>
        </div>
        {s ? <Badge tone={s.enabled ? 'success' : s.mandatory ? 'danger' : 'warning'}>{s.enabled ? t('mfa.card.on') : t('mfa.card.off')}</Badge> : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {status.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : status.isError || !s ? (
          <p className="text-sm text-danger">{t('mfa.card.loadFailed')}</p>
        ) : !s.enabled ? (
          <div className="space-y-3">
            <p className={s.mandatory ? 'text-sm font-medium text-danger' : 'text-sm text-fg-muted'}>
              {s.mandatory ? t('mfa.card.mandatory') : t('mfa.card.recommended')}
            </p>
            <Button onClick={() => setSetupOpen(true)} data-track="mfa.enable">
              <Smartphone aria-hidden />
              {t('mfa.card.enable')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 text-fg">
                <KeyRound className="size-4 text-fg-subtle" aria-hidden />
                {t('mfa.card.recoveryRemaining', { count: s.recoveryCodesRemaining })}
              </span>
              <Button size="sm" variant="secondary" onClick={() => setConfirm('regenerate')}>
                {t('mfa.card.regenerate')}
              </Button>
              {s.canDisable ? (
                <Button size="sm" variant="ghost" onClick={() => setConfirm('disable')}>
                  {t('mfa.card.disable')}
                </Button>
              ) : (
                <span className="text-xs text-fg-muted">{t('mfa.card.cannotDisable')}</span>
              )}
            </div>

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-medium text-fg">{t('mfa.devices.title')}</h3>
                  <p className="text-xs text-fg-muted">{t('mfa.devices.description')}</p>
                </div>
                {devices.data && devices.data.length > 1 ? (
                  <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables === 'all'} onClick={() => revoke.mutate('all')}>
                    {t('mfa.devices.revokeAll')}
                  </Button>
                ) : null}
              </div>
              {devices.data && devices.data.length > 0 ? (
                <ul className="divide-y divide-border rounded-(--radius-md) border border-border">
                  {devices.data.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate text-fg">{d.label ?? t('mfa.devices.unknown')}</span>
                        {d.lastUsedAt ? <span className="block text-xs text-fg-muted">{t('mfa.devices.lastUsed', { date: formatDateTime(d.lastUsedAt) })}</span> : null}
                      </span>
                      <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables === d.id} onClick={() => revoke.mutate(d.id)}>
                        {t('mfa.devices.revoke')}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-fg-muted">{t('mfa.devices.empty')}</p>
              )}
            </section>
          </div>
        )}
      </CardContent>

      {setupOpen ? (
        <SetupDialog
          onClose={() => setSetupOpen(false)}
          onEnabled={async (recoveryCodes, session) => {
            refreshSession(session);
            setSetupOpen(false);
            setCodes(recoveryCodes);
            toast.success(t('mfa.toast.enabled'));
            await refresh();
          }}
        />
      ) : null}
      {confirm ? (
        <ConfirmDialog
          kind={confirm}
          onClose={() => setConfirm(null)}
          onDone={async (newCodes) => {
            setConfirm(null);
            if (confirm === 'disable') {
              updateSession({ mfaEnabled: false });
              toast.success(t('mfa.toast.disabled'));
            } else if (newCodes) {
              setCodes(newCodes);
              toast.success(t('mfa.toast.regenerated'));
            }
            await refresh();
          }}
        />
      ) : null}
      {codes ? <RecoveryCodesDialog codes={codes} onClose={() => setCodes(null)} /> : null}
    </Card>
  );
}

function CodeInput({ id, value, onChange, label, error, allowRecovery }: { id: string; value: string; onChange: (v: string) => void; label: string; error?: string; allowRecovery?: boolean }) {
  return (
    <Field label={label} htmlFor={id} error={error}>
      <Input
        id={id}
        dir="ltr"
        autoFocus
        autoComplete="one-time-code"
        inputMode={allowRecovery ? 'text' : 'numeric'}
        maxLength={allowRecovery ? 32 : 6}
        className="tabular text-center text-lg tracking-[0.3em]"
        value={value}
        invalid={Boolean(error)}
        onChange={(e) => onChange(allowRecovery ? e.target.value : e.target.value.replace(/\D/g, ''))}
      />
    </Field>
  );
}

function SetupDialog({ onClose, onEnabled }: { onClose: () => void; onEnabled: (codes: string[], session: Awaited<ReturnType<typeof mfaApi.enable>>['session']) => Promise<void> }) {
  const { t } = useTranslation(['auth', 'common']);
  const toast = useToast();
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>();

  const setup = useMutation({ mutationFn: mfaApi.setup, onError: (e) => toast.error(t('mfa.setup.failed'), e instanceof ApiError ? e.message : undefined) });
  const { mutate: startSetup } = setup;
  // פעם אחת בלבד (StrictMode מריץ effect פעמיים בפיתוח).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    startSetup();
  }, [startSetup]);
  const secret = setup.data?.secret;
  const uri = setup.data?.otpauthUri;
  useEffect(() => {
    if (!uri) return;
    let alive = true;
    void QRCode.toDataURL(uri, { margin: 1, width: 200 }).then((url) => alive && setQr(url));
    return () => {
      alive = false;
    };
  }, [uri]);

  const enable = useMutation({
    mutationFn: () => mfaApi.enable(code),
    onSuccess: (res) => onEnabled(res.recoveryCodes, res.session),
    onError: (e) => setError(e instanceof ApiError && e.status === 400 ? t('mfa.setup.invalid') : e instanceof ApiError ? e.message : t('mfa.setup.failed')),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby="mfa-setup-desc">
        <DialogHeader>
          <DialogTitle>{t('mfa.card.enable')}</DialogTitle>
          <DialogDescription id="mfa-setup-desc">{t('mfa.card.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-fg">{t('mfa.setup.scanTitle')}</h3>
            <p className="text-xs text-fg-muted">{t('mfa.setup.scanHint')}</p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              {qr ? (
                <img src={qr} alt={t('mfa.setup.qrAlt')} width={168} height={168} className="rounded-(--radius-md) border border-border bg-white p-1" />
              ) : (
                <Skeleton className="size-[168px]" />
              )}
              {secret ? (
                <div className="min-w-0 space-y-1.5 text-xs text-fg-muted">
                  <p>{t('mfa.setup.manualKey')}</p>
                  <code dir="ltr" className="block break-all rounded-(--radius-sm) bg-surface-sunken px-2 py-1.5 font-mono text-sm text-fg">
                    {secret.replace(/(.{4})/g, '$1 ').trim()}
                  </code>
                  <Button size="sm" variant="ghost" onClick={() => void navigator.clipboard.writeText(secret)}>
                    <Copy aria-hidden />
                    {t('mfa.setup.copyKey')}
                  </Button>
                </div>
              ) : null}
            </div>
          </section>
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-fg">{t('mfa.setup.verifyTitle')}</h3>
            <p className="text-xs text-fg-muted">{t('mfa.setup.verifyHint')}</p>
            <CodeInput
              id="mfa-setup-code"
              label={t('mfa.setup.codeLabel')}
              value={code}
              error={error}
              onChange={(v) => {
                setCode(v);
                setError(undefined);
              }}
            />
          </section>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('mfa.setup.cancel')}
          </Button>
          <Button disabled={code.length !== 6 || !secret} loading={enable.isPending} onClick={() => enable.mutate()}>
            {t('mfa.setup.verify')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({ kind, onClose, onDone }: { kind: 'disable' | 'regenerate'; onClose: () => void; onDone: (codes?: string[]) => Promise<void> }) {
  const { t } = useTranslation(['auth', 'common']);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>();
  const run = useMutation({
    mutationFn: async () => {
      if (kind === 'disable') {
        await mfaApi.disable(toFactor(value));
        return undefined;
      }
      return (await mfaApi.regenerate({ code: value.replace(/\s/g, '') })).recoveryCodes;
    },
    onSuccess: (codes) => onDone(codes),
    onError: (e) => setError(e instanceof ApiError && e.status === 400 ? t('mfa.step.invalid') : e instanceof ApiError ? e.message : t('mfa.toast.failed')),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby="mfa-confirm-desc">
        <DialogHeader>
          <DialogTitle>{t(`mfa.${kind}.title`)}</DialogTitle>
          <DialogDescription id="mfa-confirm-desc">{t(`mfa.${kind}.description`)}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <CodeInput
            id="mfa-confirm-code"
            label={t(`mfa.${kind}.codeLabel`)}
            value={value}
            error={error}
            allowRecovery={kind === 'disable'}
            onChange={(v) => {
              setValue(v);
              setError(undefined);
            }}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant={kind === 'disable' ? 'danger' : 'primary'} disabled={value.trim().length < 6} loading={run.isPending} onClick={() => run.mutate()}>
            {t(`mfa.${kind}.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecoveryCodesDialog({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const { t } = useTranslation(['auth', 'common']);
  const toast = useToast();
  const text = `${t('mfa.codes.fileHeader')}\n\n${codes.join('\n')}\n`;
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'craftmind-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    // אין סגירה בלחיצה בחוץ — שלא ייסגר לפני שנשמרו.
    <Dialog open>
      <DialogContent aria-describedby="mfa-codes-desc" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('mfa.codes.title')}</DialogTitle>
          <DialogDescription id="mfa-codes-desc">{t('mfa.codes.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <ul dir="ltr" className="grid grid-cols-2 gap-2 rounded-(--radius-md) bg-surface-sunken p-3 font-mono text-sm text-fg">
            {codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(codes.join('\n'));
                toast.success(t('mfa.codes.copied'));
              }}
            >
              <Copy aria-hidden />
              {t('mfa.codes.copy')}
            </Button>
            <Button size="sm" variant="secondary" onClick={download}>
              <Download aria-hidden />
              {t('mfa.codes.download')}
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>{t('mfa.codes.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
